"use client"

import { startTransition, useEffect } from "react"
import { useRouter } from "next/navigation"

/**
 * App segment error boundary - catches errors within /app/* routes.
 *
 * Provides manual recovery via "Try again" (reset + router.refresh)
 * and full page reload. Note: RSC header parse errors (E10) are
 * handled by Next.js MPA fallback before reaching this boundary.
 */
export default function AppError({
    error,
    reset
}: {
    error: Error & { digest?: string }
    reset: () => void
}) {
    const router = useRouter()

    useEffect(() => {
        console.error("[AppError]", error.message, error.digest)
    }, [error])

    return (
        <div className="flex min-h-[60vh] items-center justify-center">
            <div className="mx-auto max-w-md px-6 py-16 text-center">
                <div className="mb-6 flex justify-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                        <svg className="h-7 w-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                        </svg>
                    </div>
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Something went wrong</h2>
                <p className="mt-2 text-[15px] text-gray-500">
                    An unexpected error occurred. Try refreshing.
                </p>
                <div className="mt-6 flex items-center justify-center gap-3">
                    <button
                        onClick={() => startTransition(() => { router.refresh(); reset() })}
                        className="rounded-xl bg-gray-900 px-5 py-2.5 text-[14px] font-medium text-white transition-colors hover:bg-gray-800"
                    >
                        Try again
                    </button>
                    <button
                        onClick={() => window.location.reload()}
                        className="rounded-xl bg-gray-100 px-5 py-2.5 text-[14px] font-medium text-gray-700 transition-colors hover:bg-gray-200"
                    >
                        Reload page
                    </button>
                </div>
            </div>
        </div>
    )
}
